import {
  Box,
  Button,
  createStyles,
  Paper,
  Group,
  LoadingOverlay,
  Stack,
  Text,
} from '@mantine/core';
import React from 'react';
import ReactMarkdown from 'react-markdown';

import { usePartnerConfig, useSendTestEmail } from '@portals/api/partners';
import { ReactComponent as Polygon } from '@portals/icons/bold/Polygon 2.svg';
import { ReactComponent as SmsTracking } from '@portals/icons/linear/sms-tracking.svg';

import { useBrandingContext } from '../branding.context';

export function EmailsPreview() {
  const { classes } = useStyles();
  const sendTestEmail = useSendTestEmail();
  const { portalConfig, defaultValues } = useBrandingContext();

  const config = usePartnerConfig();

  const footerLabel =
    portalConfig?.email_settings?.markdown ??
    defaultValues.email_settings?.markdown;

  const onSendTestEmail = async () => {
    try {
      await sendTestEmail.mutateAsync({
        ...portalConfig?.email_settings,
      });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <LoadingOverlay visible={sendTestEmail.isLoading} />

      <Paper>
        <Box p={16}>
          <Stack spacing={4}>
            <Group spacing="xs">
              <Text weight={700}>
                {portalConfig.email_settings?.custom_from_email
                  ? config.display_name
                  : 'Xyte'}
              </Text>

              <Text weight={500} color="gray.6">
                {portalConfig.email_settings?.custom_from_email
                  ? `<${config.name}@on-xyte.io>`
                  : `<info@xyte.io>`}
              </Text>
            </Group>

            <Group spacing={4}>
              <Text color="gray.6">to me</Text>
              <Polygon />
            </Group>
          </Stack>
        </Box>

        <Stack pos="relative">
          <svg
            width="580"
            height="580"
            viewBox="0 0 580 580"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g filter="url(#filter0_d_2220_26512)">
              {/* Header */}
              <path
                d="M18 14C18 10.134 21.134 7 25 7H555C558.866 7 562 10.134 562 14V14H18V14Z"
                fill={
                  portalConfig?.email_settings?.color ||
                  defaultValues.email_settings.color
                }
              />
              {/* Header */}

              <mask id="path-2-inside-1_2220_26512" fill="white">
                <path d="M18 14H562V155H18V14Z" />
              </mask>

              <path d="M18 14H562V155H18V14Z" fill="white" />

              {/* Logo */}
              <rect
                x="42"
                y="38"
                width="91"
                height="93"
                rx="5.00633"
                fill="url(#pattern0)"
              />
              {/* Logo */}

              <path
                d="M562 154H18V156H562V154Z"
                fill="#EEEEEE"
                mask="url(#path-2-inside-1_2220_26512)"
              />
              <rect
                width="544"
                height="64"
                transform="translate(18 155)"
                fill="white"
              />

              {/* Title */}
              <rect
                x="42"
                y="179"
                width="211"
                height="16"
                rx="8"
                fill={
                  portalConfig?.email_settings?.primary ||
                  defaultValues.email_settings.primary
                }
              />
              <rect
                x="263"
                y="179"
                width="84"
                height="16"
                rx="8"
                fill={
                  portalConfig?.email_settings?.primary ||
                  defaultValues.email_settings.primary
                }
              />
              {/* Title */}

              <line x1="42" y1="218.5" x2="538" y2="218.5" stroke="#EEEEEE" />
              <mask id="path-8-inside-2_2220_26512" fill="white">
                <path d="M18 219H562V471H18V219Z" />
              </mask>
              <path d="M18 219H562V471H18V219Z" fill="white" />

              {/* Email body */}
              <rect
                x="42"
                y="243"
                width="71"
                height="6"
                rx="3"
                fill={
                  portalConfig?.email_settings?.secondary ||
                  defaultValues.email_settings.secondary
                }
              />
              <rect
                x="42"
                y="257"
                width="423"
                height="6"
                rx="3"
                fill={
                  portalConfig?.email_settings?.secondary ||
                  defaultValues.email_settings.secondary
                }
              />
              <rect
                x="42"
                y="271"
                width="321"
                height="6"
                rx="3"
                fill={
                  portalConfig?.email_settings?.secondary ||
                  defaultValues.email_settings.secondary
                }
              />
              <rect
                x="42"
                y="285"
                width="351"
                height="6"
                rx="3"
                fill={
                  portalConfig?.email_settings?.secondary ||
                  defaultValues.email_settings.secondary
                }
              />
              <rect
                x="42"
                y="299"
                width="408"
                height="6"
                rx="3"
                fill={
                  portalConfig?.email_settings?.secondary ||
                  defaultValues.email_settings.secondary
                }
              />
              <rect
                x="42"
                y="313"
                width="321"
                height="6"
                rx="3"
                fill={
                  portalConfig?.email_settings?.secondary ||
                  defaultValues.email_settings.secondary
                }
              />
              <rect
                x="228"
                y="343"
                width="124"
                height="35"
                rx="8"
                fill={
                  portalConfig?.email_settings?.tertiary ||
                  defaultValues.email_settings.tertiary
                }
              />
              <rect
                x="263"
                y="357"
                width="54"
                height="7"
                rx="3.5"
                fill="white"
              />
              <rect
                x="42"
                y="402"
                width="254"
                height="6"
                rx="3"
                fill={
                  portalConfig?.email_settings?.secondary ||
                  defaultValues.email_settings.secondary
                }
              />
              <rect
                x="42"
                y="416"
                width="423"
                height="6"
                rx="3"
                fill={
                  portalConfig?.email_settings?.secondary ||
                  defaultValues.email_settings.secondary
                }
              />
              <rect
                x="42"
                y="430"
                width="321"
                height="6"
                rx="3"
                fill={
                  portalConfig?.email_settings?.secondary ||
                  defaultValues.email_settings.secondary
                }
              />
              {/* Email body */}

              {/* Footer */}
              <path
                d="M562 470H18V472H562V470Z"
                fill="#EEEEEE"
                mask="url(#path-8-inside-2_2220_26512)"
              />
              <mask id="path-21-inside-3_2220_26512" fill="white">
                <path d="M18 471H562V527C562 531.418 558.418 535 554 535H26C21.5817 535 18 531.418 18 527V471Z" />
              </mask>
              {/*<path*/}
              {/*  d="M18 471H562V527C562 531.418 558.418 535 554 535H26C21.5817 535 18 531.418 18 527V471Z"*/}
              {/*  fill="white"*/}
              {/*/>*/}

              {/* Footer text */}
              <foreignObject
                className="node"
                x={18}
                y={470}
                width={544}
                height={85}
              >
                <Box w={544} h={85} className={classes.markdownWrapper}>
                  <ReactMarkdown className={classes.markdown}>
                    {footerLabel}
                  </ReactMarkdown>
                </Box>
              </foreignObject>
              {/* Footer */}
            </g>

            <defs>
              <filter
                id="filter0_d_2220_26512"
                x="0"
                y="0"
                width="580"
                height="580"
                filterUnits="userSpaceOnUse"
                colorInterpolationFilters="sRGB"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                />
                <feOffset dy="11" />
                <feGaussianBlur stdDeviation="9" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix
                  type="matrix"
                  values="0 0 0 0 0.14902 0 0 0 0 0.196078 0 0 0 0 0.219608 0 0 0 0.1 0"
                />
                <feBlend
                  mode="normal"
                  in2="BackgroundImageFix"
                  result="effect1_dropShadow_2220_26512"
                />
                <feBlend
                  mode="normal"
                  in="SourceGraphic"
                  in2="effect1_dropShadow_2220_26512"
                  result="shape"
                />
              </filter>
              <pattern
                id="pattern0"
                patternContentUnits="objectBoundingBox"
                width="1"
                height="1"
              >
                <use
                  xlinkHref="#image0_2220_26512"
                  transform="matrix(0.00510989 0 0 0.005 -0.010989 0)"
                />
              </pattern>

              {/* Logo */}
              <image
                id="image0_2220_26512"
                width="200"
                height="200"
                href={portalConfig?.email_settings?.logo || 'logo_yellow.png'}
              />
              {/* Logo */}
            </defs>
          </svg>
        </Stack>
      </Paper>

      <Group position="center">
        <Button
          variant="default"
          onClick={onSendTestEmail}
          leftIcon={<SmsTracking width={18} height={18} />}
        >
          Test Email
        </Button>
      </Group>
    </>
  );
}

const useStyles = createStyles((theme) => ({
  markdownWrapper: {
    border: `1px solid ${theme.colors.gray[2]}`,
    background: theme.white,
    padding: theme.spacing.md,
    paddingBottom: 0,
    overflow: 'auto',
    borderBottomLeftRadius: theme.radius.md,
    borderBottomRightRadius: theme.radius.md,
  },
  markdown: {
    width: '100%',
  },
}));
